import { useState, useRef, useCallback, useEffect } from 'react'
import { X, Move, RotateCcw, Check, Loader } from 'lucide-react'

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

interface ImageCropModalProps {
  isOpen: boolean
  imageFile: File
  onClose: () => void
  onCropComplete: (croppedFile: File) => void
  aspectRatio?: number // Optional aspect ratio (width/height)
}

export default function ImageCropModal({
  isOpen,
  imageFile,
  onClose,
  onCropComplete,
  aspectRatio = 1 // Default to square (1:1)
}: ImageCropModalProps) {
  const [imageUrl, setImageUrl] = useState<string>('')
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 200, height: 200 })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [processing, setProcessing] = useState(false)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Load image when modal opens
  useEffect(() => {
    if (isOpen && imageFile) {
      const url = URL.createObjectURL(imageFile)
      setImageUrl(url)
      
      // Clean up URL when component unmounts
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [isOpen, imageFile])

  // Initialize crop area when image loads
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current) return

    const img = imageRef.current
    const imgWidth = img.offsetWidth
    const imgHeight = img.offsetHeight
    
    setImageDimensions({ width: imgWidth, height: imgHeight })

    // Calculate initial crop area (centered, 80% of smaller dimension)
    const minDimension = Math.min(imgWidth, imgHeight)
    const cropSize = Math.min(minDimension * 0.8, 300)
    
    // Ensure aspect ratio
    let cropWidth = cropSize
    let cropHeight = cropSize
    
    if (aspectRatio > 1) {
      cropHeight = cropSize / aspectRatio
    } else if (aspectRatio < 1) {
      cropWidth = cropSize * aspectRatio
    }

    setCropArea({
      x: (imgWidth - cropWidth) / 2,
      y: (imgHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    })
  }, [aspectRatio])

  // Handle crop area dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - cropArea.x,
      y: e.clientY - cropArea.y
    })
  }, [cropArea])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !imageRef.current) return

    const img = imageRef.current
    const rect = img.getBoundingClientRect()
    
    const newX = Math.max(0, Math.min(e.clientX - rect.left - dragStart.x, imageDimensions.width - cropArea.width))
    const newY = Math.max(0, Math.min(e.clientY - rect.top - dragStart.y, imageDimensions.height - cropArea.height))

    setCropArea(prev => ({
      ...prev,
      x: newX,
      y: newY
    }))
  }, [isDragging, dragStart, imageDimensions, cropArea.width, cropArea.height])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setIsResizing(false)
  }, [])

  // Handle resize handles
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    setDragStart({ x: e.clientX, y: e.clientY })
  }, [])

  // Crop the image
  const handleCrop = useCallback(async () => {
    if (!imageRef.current || !canvasRef.current) return

    setProcessing(true)

    try {
      const img = imageRef.current
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Could not get canvas context')
      }

      // Calculate the scale factor between displayed image and actual image
      const scaleX = img.naturalWidth / img.offsetWidth
      const scaleY = img.naturalHeight / img.offsetHeight
      
      console.log('📐 Scale factors:', { scaleX, scaleY })
      console.log('📐 Image dimensions - Natural:', img.naturalWidth, 'x', img.naturalHeight)
      console.log('📐 Image dimensions - Display:', img.offsetWidth, 'x', img.offsetHeight)

      // Set canvas size to crop area size
      canvas.width = cropArea.width * scaleX
      canvas.height = cropArea.height * scaleY

      // Create a new image from the file to get the full resolution
      const fullResImage = new Image()
      fullResImage.onload = () => {
        console.log('🖼️ Full resolution image loaded:', fullResImage.naturalWidth, 'x', fullResImage.naturalHeight)
        console.log('🎯 Crop area (display coordinates):', cropArea)
        console.log('📏 Canvas size:', canvas.width, 'x', canvas.height)
        
        // Calculate source coordinates in the full resolution image
        const sourceX = cropArea.x * scaleX
        const sourceY = cropArea.y * scaleY
        const sourceWidth = cropArea.width * scaleX
        const sourceHeight = cropArea.height * scaleY
        
        console.log('🎯 Source crop coordinates:', { sourceX, sourceY, sourceWidth, sourceHeight })
        
        // Draw the cropped portion
        ctx.drawImage(
          fullResImage,
          sourceX, // source x
          sourceY, // source y  
          sourceWidth, // source width
          sourceHeight, // source height
          0, // destination x
          0, // destination y
          canvas.width, // destination width
          canvas.height // destination height
        )

        console.log('✅ Canvas drawing complete')

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            console.log('📦 Blob created:', blob.size, 'bytes, type:', blob.type)
            
            // Create a new File from the blob
            const croppedFile = new File([blob], imageFile.name, {
              type: imageFile.type,
              lastModified: Date.now()
            })
            
            console.log('📁 Cropped file created:', croppedFile.name, croppedFile.size, 'bytes')
            
            onCropComplete(croppedFile)
            onClose()
          } else {
            console.error('❌ Failed to create blob from canvas')
          }
          setProcessing(false)
        }, imageFile.type, 0.9)
      }
      
      fullResImage.onerror = (error) => {
        console.error('❌ Error loading full resolution image:', error)
        setProcessing(false)
      }

      fullResImage.src = imageUrl
    } catch (error) {
      console.error('Error cropping image:', error)
      setProcessing(false)
    }
  }, [imageFile, imageUrl, cropArea, onCropComplete, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Crop Profile Picture</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={processing}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Image cropping area */}
            <div className="flex-1">
              <div 
                ref={containerRef}
                className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50"
                style={{ minHeight: '400px' }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {imageUrl && (
                  <>
                    <img
                      ref={imageRef}
                      src={imageUrl}
                      alt="Crop preview"
                      className="max-w-full max-h-[400px] object-contain"
                      onLoad={handleImageLoad}
                      draggable={false}
                    />
                    
                    {/* Crop overlay */}
                    {imageDimensions.width > 0 && (
                      <div
                        className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move"
                        style={{
                          left: cropArea.x,
                          top: cropArea.y,
                          width: cropArea.width,
                          height: cropArea.height,
                        }}
                        onMouseDown={handleMouseDown}
                      >
                        {/* Resize handles */}
                        <div
                          className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 border border-white cursor-se-resize"
                          onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                        />
                        <div
                          className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border border-white cursor-ne-resize"
                          onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
                        />
                        <div
                          className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 border border-white cursor-sw-resize"
                          onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
                        />
                        <div
                          className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 border border-white cursor-nw-resize"
                          onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
                        />
                        
                        {/* Move icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Move className="w-6 h-6 text-blue-600 opacity-75" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Crop preview */}
            <div className="lg:w-64">
              <div className="text-sm text-gray-600 mb-2">Preview:</div>
              <div className="w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 mx-auto">
                {imageUrl && imageDimensions.width > 0 && (
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      backgroundPosition: `${-cropArea.x * (128 / cropArea.width)}px ${-cropArea.y * (128 / cropArea.height)}px`,
                      backgroundSize: `${imageDimensions.width * (128 / cropArea.width)}px ${imageDimensions.height * (128 / cropArea.height)}px`,
                    }}
                  />
                )}
              </div>
              
              <div className="mt-4 text-sm text-gray-600">
                <div>Crop area: {Math.round(cropArea.width)} × {Math.round(cropArea.height)}</div>
                <div>Aspect ratio: {aspectRatio === 1 ? 'Square' : `${aspectRatio.toFixed(2)}:1`}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Drag the blue area to position your image, then click Apply Crop
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCrop}
              disabled={processing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {processing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Apply Crop</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Hidden canvas for cropping */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  )
}